import React, { useMemo, useState } from 'react';
import Seo from '../components/Seo';
import { shopCatalog, whatsappOrderUrl, ShopProduct } from '../data/smartHomeCatalog';
import './ShopPage.css';

const statusLabel: Record<ShopProduct['status'], string> = {
  pi: 'على فاتورة أولية',
  requested: 'مطلوب / بانتظار رد',
  inquiry: 'استعلام / كتالوج',
};

const ShopPage: React.FC = () => {
  const { brand, categories, products } = shopCatalog;
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (activeCategory !== 'all' && p.category !== activeCategory) return false;
      if (activeStatus !== 'all' && p.status !== activeStatus) return false;
      if (!q) return true;
      const hay = `${p.nameAr} ${p.nameEn} ${p.model} ${p.vendor} ${p.protocol.join(' ')} ${p.piRef ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [activeCategory, activeStatus, products, query]);

  const generalOrderUrl = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(
    'مرحباً الشراع — أريد قائمة كاملة وسعراً لأجهزة المنزل الذكي المعروضة.',
  )}`;

  const piCount = products.filter((p) => p.status === 'pi').length;
  const reqCount = products.filter((p) => p.status === 'requested').length;

  return (
    <main className="shop-page">
      <Seo
        title="الشراع — كتالوج المنزل الذكي | طلب واتساب"
        description="كتالوج كامل لكل أجهزة المنزل الذكي التي طلبناها من مصانع الصين. الطلب عبر واتساب فقط."
        path="/shop"
        lang="ar"
        imageUrl="/shop/products/bulb-zigbee-e27.png"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: brand.nameAr,
          numberOfItems: products.length,
          url: 'https://alshiraa-customs.com/shop',
        }}
      />

      <section className="shop-hero">
        <div className="container shop-hero-grid">
          <div className="shop-hero-copy">
            <p className="shop-kicker">كتالوج الطلبات · اللاذقية · الصين → AY → سوريا</p>
            <h1 className="shop-brand">{brand.nameAr}</h1>
            <p className="shop-lead">
              كل صنف طلبناه من المصانع — حتى ما لم يصل رده بعد. الصور توضيحية للعرض؛ الطلب والسعر عبر واتساب فقط.
            </p>
            <div className="shop-hero-actions">
              <a className="shop-wa-btn shop-wa-btn--primary" href={generalOrderUrl} target="_blank" rel="noreferrer">
                اطلب عبر واتساب
              </a>
              <a className="shop-wa-btn shop-wa-btn--ghost" href={`tel:+${brand.whatsapp}`}>
                {brand.whatsappDisplay}
              </a>
            </div>
            <p className="shop-note">{brand.noteAr}</p>
          </div>
          <aside className="shop-hero-stats" aria-label="ملخص الكتالوج">
            <div>
              <strong>{products.length}</strong>
              <span>صنف في الكتالوج</span>
            </div>
            <div>
              <strong>{piCount}</strong>
              <span>على PI وارد</span>
            </div>
            <div>
              <strong>{reqCount}</strong>
              <span>مطلوب / معلّق</span>
            </div>
          </aside>
        </div>
      </section>

      <section className="shop-toolbar">
        <div className="container shop-toolbar-inner">
          <div className="shop-filters" role="tablist" aria-label="تصنيفات">
            <button
              type="button"
              className={`shop-chip ${activeCategory === 'all' ? 'is-active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              الكل ({products.length})
            </button>
            {categories.map((cat) => {
              const count = products.filter((p) => p.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`shop-chip ${activeCategory === cat.id ? 'is-active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.nameAr} ({count})
                </button>
              );
            })}
          </div>
          <div className="shop-status-row">
            <button
              type="button"
              className={`shop-status ${activeStatus === 'all' ? 'is-active' : ''}`}
              onClick={() => setActiveStatus('all')}
            >
              كل الحالات
            </button>
            <button
              type="button"
              className={`shop-status ${activeStatus === 'pi' ? 'is-active' : ''}`}
              onClick={() => setActiveStatus('pi')}
            >
              على PI
            </button>
            <button
              type="button"
              className={`shop-status ${activeStatus === 'requested' ? 'is-active' : ''}`}
              onClick={() => setActiveStatus('requested')}
            >
              مطلوب
            </button>
            <button
              type="button"
              className={`shop-status ${activeStatus === 'inquiry' ? 'is-active' : ''}`}
              onClick={() => setActiveStatus('inquiry')}
            >
              استعلام
            </button>
            <label className="shop-search">
              <span className="sr-only">بحث</span>
              <input
                type="search"
                placeholder="ابحث بالموديل أو المورد أو البروتوكول…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>
        </div>
      </section>

      <section className="shop-grid-section">
        <div className="container">
          <p className="shop-result-count">عرض {filtered.length} من {products.length}</p>
          {filtered.length === 0 ? (
            <p className="shop-empty">لا نتائج — عدّل الفلتر أو راسلنا على واتساب.</p>
          ) : (
            <div className="shop-grid">
              {filtered.map((product) => (
                <ProductCard key={product.id} product={product} phone={brand.whatsapp} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="shop-cta">
        <div className="container shop-cta-inner">
          <h2>اطلب الموديل والكمية على واتساب</h2>
          <p>أرسل اسم المنتج أو رقم الموديل — نؤكد السعر بالليرة والتوفر خلال ساعات العمل.</p>
          <a className="shop-wa-btn shop-wa-btn--primary" href={generalOrderUrl} target="_blank" rel="noreferrer">
            تواصل واتساب الآن
          </a>
        </div>
      </section>
    </main>
  );
};

const ProductCard: React.FC<{ product: ShopProduct; phone: string }> = ({ product, phone }) => {
  const orderUrl = whatsappOrderUrl(product, phone);
  return (
    <article className={`shop-card shop-card--${product.status}`}>
      <div className="shop-card-media">
        <img src={product.image} alt={product.nameAr} loading="lazy" width={640} height={640} />
        <span className="shop-badge">{product.badgeAr}</span>
      </div>
      <div className="shop-card-body">
        <div className="shop-card-meta">
          <span className="shop-card-vendor">{product.vendor}</span>
          <span className={`shop-card-status shop-card-status--${product.status}`}>
            {statusLabel[product.status]}
          </span>
        </div>
        <h3 className="shop-card-title">{product.nameAr}</h3>
        <p className="shop-card-model">{product.model}</p>
        {product.qtyLabel && <p className="shop-card-qty">الكمية المرجعية: {product.qtyLabel}</p>}
        <p className="shop-card-blurb">{product.blurbAr}</p>
        <ul className="shop-protocols">
          {product.protocol.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <a className="shop-wa-btn shop-wa-btn--card" href={orderUrl} target="_blank" rel="noreferrer">
          اطلب عبر واتساب
        </a>
      </div>
    </article>
  );
};

export default ShopPage;
